<template>
  <span class="job-list-time-entry simple-actions-row">
    <job-list-users
      :proposal_id="proposal_id"
      :default_job_list_id="job_list_id"
      @users-updated="usersUpdated"
    />

    <el-button
      type="primary"
      size="mini"
      icon="el-icon-time"
      @click.stop="openDialog"
    >
      Add Time
    </el-button>

    <el-dialog
      :title="`Add Time - ${list_name}`"
      :visible.sync="dialogVisible"
      width="950px"
      append-to-body
      @opened="set_default_input()"
    >
      <div>
        <el-date-picker
          v-model="workDate"
          type="date"
          format="dd/MM/yyyy"
          value-format="yyyy-MM-dd"
          placeholder="Work date"
          size="small"
          style="margin-bottom: 15px"
        />

        <div
          v-for="task in timeTasks"
          :key="task.job_proposal_id"
          class="time-task-block"
        >
          <p>{{ task.name }}</p>

          <el-table
            :data="task.users"
            border
            size="small"
            style="width: 100%"
          >
            <el-table-column
              label="Employee"
              min-width="220"
            >
              <template slot-scope="scope">
                <span :class="{ 'team-leader-name': scope.row.is_team_leader }">
                  {{ scope.row.name }}
                </span>

                <el-tag
                  v-if="scope.row.is_team_leader"
                  type="success"
                  size="mini"
                  class="team-leader-tag"
                >
                  Team Leader
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column
              label="Current"
              width="140"
            >
              <template slot-scope="scope">
                {{ formatMinutes(scope.row.current_time_in_minutes) }}
              </template>
            </el-table-column>

            <el-table-column
              label="Add Minutes"
              width="160"
            >
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.entry_time_in_minutes"
                  :min="0"
                  :step="5"
                  controls-position="right"
                  size="small"
                  style="width: 120px"
                />
              </template>
            </el-table-column>

            <el-table-column
              label="Add Hours"
              width="100"
            >
              <template slot-scope="scope">
                {{ minutesToHours(scope.row.entry_time_in_minutes) }}
              </template>
            </el-table-column>

            <el-table-column
              label="Note"
              min-width="280"
            >
              <template slot-scope="scope">
                <div
                  v-if="scope.row.current_description"
                  class="current-note"
                >
                  {{ scope.row.current_description }}
                </div>

                <el-input
                  v-model="scope.row.entry_description"
                  size="small"
                  placeholder="New optional note"
                />
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="summary">
          <strong>New Entry Total:</strong> {{ formatMinutes(totalMinutes) }}
        </div>
      </div>

      <span slot="footer">
        <el-button
          size="mini"
          @click="dialogVisible = false"
        >
          Cancel
        </el-button>

        <el-button
          type="primary"
          size="mini"
          :loading="saving"
          @click="saveTime"
        >
          Save Time
        </el-button>
      </span>
    </el-dialog>
  </span>
</template>

<script>
import JobListUsers from './JobListUsers.vue';
export default {
  name: 'JobListTimeEntry',
  components: {JobListUsers},

  props: {
    list_name: {
      type: String,
      required: true
    },

    jobs: {
      type: Array,
      required: true
    },

    proposal_id: {
      type: [String, Number],
      required: true
    },

    job_list_id: {
      type: [String, Number],
      required: false,
      default: null
    }
  },

  data() {
    return {
      dialogVisible: false,
      saving: false,
      workDate: this.today(),
      timeTasks: []
    };
  },

  computed: {
    totalMinutes() {
      let total = 0;

      this.timeTasks.forEach((task) => {
        task.users.forEach((user) => {
          total += Number(user.entry_time_in_minutes || 0);
        });
      });

      return total;
    }
  },

  methods: {
    usersUpdated() {
      this.$emit('users-updated');
    },
    openDialog() {
      this.workDate = this.today();

      const seenJobProposalIds = {};

      this.timeTasks = this.jobs
        .map((job) => {
          return {
            job_proposal_id: job.id || job.job_proposal_id,
            proposal_id: job.proposal_id || this.proposal_id,
            name: job.name,
            users: this.normaliseUsers(job)
          };
        })
        .filter((task) => {
          if (!task.job_proposal_id) return false;
          if (task.users.length === 0) return false;

          if (seenJobProposalIds[task.job_proposal_id]) return false;

          seenJobProposalIds[task.job_proposal_id] = true;
          return true;
        });

      if (this.timeTasks.length === 0) {
        this.$message.warning('No assigned users found for this list.');
        return;
      }

      this.dialogVisible = true;
    },

    normaliseUsers(job) {
      if (Array.isArray(job.simple_job_users_data)) {
        return job.simple_job_users_data.map((user) => {
          return {
            crm_user_id: user.crm_user_id || user.id,
            name: user.name,
            current_time_in_minutes: Number(user.time_in_minutes || 0),
            current_description: user.description || '',
            entry_time_in_minutes: 0,
            entry_description: '',
            is_team_leader: !!user.is_team_leader
          };
        });
      }

      return [];
    },

    saveTime() {
      const timeEntries = [];

      this.timeTasks.forEach((task) => {
        task.users.forEach((user) => {
          const minutes = Number(user.entry_time_in_minutes || 0);

          if (minutes > 0) {
            timeEntries.push({
              job_proposal_id: task.job_proposal_id,
              proposal_id: task.proposal_id,
              crm_user_id: user.crm_user_id,
              time_in_minutes: minutes,
              description: user.entry_description
            });
          }
        });
      });

      if (timeEntries.length === 0) {
        this.$message.warning('Enter minutes for at least one employee.');
        return;
      }

      this.saving = true;

      this.$http.post('/time_spents/save_simple_time_entries', {
        proposal_id: this.proposal_id,
        work_date: this.workDate,
        entry_mode: 'simple',
        time_entries: timeEntries
      })
        .then(() => {
          this.$message.success('Time saved.');
          this.dialogVisible = false;
          this.$emit('saved');
        })
        .catch((error) => {
          console.error(error);

          const response = error.body || (error.response && error.response.data) || {};
          const message = response.error || 'Could not save time.';

          this.$message.error(message);
        })
        .finally(() => {
          this.saving = false;
        });
    },

    formatMinutes(minutes) {
      const mins = Number(minutes || 0);
      const hours = Math.floor(mins / 60);
      const remaining = mins % 60;

      return `${mins}m | ${hours}h ${remaining}m`;
    },

    minutesToHours(minutes) {
      return (Number(minutes || 0) / 60).toFixed(2);
    },

    today() {
      const date = new Date();
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');

      return `${yyyy}-${mm}-${dd}`;
    },

    set_default_input(mills = 300) {
      setTimeout(function() {
        Array.from(document.querySelectorAll('.el-input__inner')).forEach(
          function(e) {
            e.classList.add('browser-default');
            if (e.querySelector('input') != null)
              e.querySelector('input').className += ' browser-default';
          }
        );
      }, mills);
    }
  }
};
</script>

<style scoped>
.job-list-time-entry {
  display: inline-block;
}

.time-task-block {
  margin-bottom: 25px;
}

.time-task-block h4 {
  margin: 10px 0;
}

.summary {
  margin-top: 15px;
  text-align: right;
}

.team-leader-name {
  color: #67c23a;
  font-weight: 600;
}

.team-leader-tag {
  margin-left: 6px;
}

.current-note {
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}

.simple-actions-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
